import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  type MouseEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';

import '@cartwhl/ui/atoms/Dropdown.css';
import Text from '@cartwhl/ui/base/TextNew';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import EyeIcon from '@cartwhl/ui/icons/EyeIcon';
import LearnIcon from '@cartwhl/ui/icons/LearnIcon';
import LinkIcon from '@cartwhl/ui/icons/LinkIcon';
import ThreeDotsIcon from '@cartwhl/ui/icons/ThreeDotsIcon';
import TrashIcon from '@cartwhl/ui/icons/TrashIcon';

import {
  ASSET_LIBRARY_DELETE_OWN_ITEM_TOOLTIP,
  ASSET_LIBRARY_RESTORE_OWN_ITEM_TOOLTIP,
} from '../assetLibraryAssetLifecycle';
import {
  type AssetLibraryCardMoreMenuState,
  type AssetLibraryOpenWithOption,
  resolveAssetLibraryCardMoreMenu,
} from '../assetLibraryOpenWith';
import {
  buildAssetLibraryShareUrl,
  copyAssetLibraryShareLink,
} from '../assetLibraryShareLink';
import { useStore } from '../assetLibraryStore';
import { useAssetLibraryAssetActions } from '../hooks/useAssetLibraryAssetActions';
import { useAssetLibraryOpenWithActions } from '../hooks/useAssetLibraryOpenWithActions';
import type { AssetLibraryDisplayItem } from '../utils';
import AssetLibraryBlockedActionTooltip from './AssetLibraryTrashActionTooltip';

const CARD_ACTION_BUTTON_CLASS =
  'border-primary bg-primary hover:bg-secondary flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg border shadow-sm transition-colors';

const MENU_CONTENT_CLASS =
  'RxDropdownContent shadow-menu w-[260px] max-w-[260px] rounded-2xl bg-white p-2 text-gray-90 dark:bg-gray-75 dark:text-white';

const OPEN_WITH_THUMBNAIL_CLASS =
  'h-14 w-[calc(3.5rem*0.65)] shrink-0 overflow-hidden rounded-lg object-cover object-center';

function OpenWithAppVideoThumbnail({
  src,
  hovered,
}: {
  src: string;
  hovered: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { ref: inViewRef, inView } = useInView({
    rootMargin: '200px',
    threshold: 0.1,
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !inView) return;
    if (hovered) {
      void video.play();
    } else {
      video.pause();
    }
  }, [hovered, inView]);

  return (
    <div ref={inViewRef} className={OPEN_WITH_THUMBNAIL_CLASS}>
      {inView ? (
        <video
          ref={videoRef}
          src={src}
          className="h-full w-full rounded-[inherit] object-cover"
          muted
          playsInline
          loop
          disablePictureInPicture
        />
      ) : null}
    </div>
  );
}

function OpenWithAppThumbnail({
  src,
  hovered = false,
}: {
  src: string;
  hovered?: boolean;
}) {
  if (src.endsWith('.webm') || src.endsWith('.mp4')) {
    return <OpenWithAppVideoThumbnail src={src} hovered={hovered} />;
  }

  return <img src={src} alt="" className={OPEN_WITH_THUMBNAIL_CLASS} />;
}

function OpenWithAppRow({
  label,
  subtitle,
  imageSrc,
  suffix,
}: {
  label: string;
  subtitle: string;
  imageSrc: string;
  suffix?: ReactNode;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="grid w-full max-w-full grid-cols-[calc(3.5rem*0.65)_minmax(0,1fr)_auto] items-start gap-x-3 px-3 py-2"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <OpenWithAppThumbnail src={imageSrc} hovered={hovered} />
      <div className="flex min-w-0 flex-col gap-2 self-start">
        <Text
          as="div"
          token="14/medium"
          leading="title"
          className="text-primary pt-1"
        >
          {label}
        </Text>
        <Text as="div" token="14/medium" className="text-tertiary break-words">
          {subtitle}
        </Text>
      </div>
      {suffix != null ? (
        <span className="flex shrink-0 items-center justify-self-end pt-0.5">
          {suffix}
        </span>
      ) : null}
    </div>
  );
}

function OpenWithSingleOption({
  option,
  onClose,
}: {
  option: Extract<
    AssetLibraryOpenWithOption,
    { app: 'motion-editor' | 'comic' }
  >;
  onClose: () => void;
}) {
  return (
    <DropdownMenu.Item
      className="hover:bg-gray-15 dark:hover:bg-gray-90 w-full max-w-full cursor-pointer rounded-lg whitespace-normal outline-hidden transition-colors"
      onSelect={() => {
        option.onSelect();
        onClose();
      }}
    >
      <OpenWithAppRow
        label={option.label}
        subtitle={option.subtitle}
        imageSrc={option.imageSrc}
      />
    </DropdownMenu.Item>
  );
}

function OpenWithMascotOption({
  option,
  onClose,
}: {
  option: Extract<AssetLibraryOpenWithOption, { app: 'mascot' }>;
  onClose: () => void;
}) {
  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger className="hover:bg-gray-15 dark:hover:bg-gray-90 data-[state=open]:bg-gray-15 dark:data-[state=open]:bg-gray-90 w-full max-w-full cursor-pointer rounded-lg whitespace-normal outline-hidden transition-colors">
        <OpenWithAppRow
          label={option.label}
          subtitle={option.subtitle}
          imageSrc={option.imageSrc}
          suffix={<ChevronRightIcon className="text-gray-45 h-4 w-4" />}
        />
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          className="RxSubContent text-gray-90 shadow-menu dark:bg-gray-75 flex min-w-[240px] flex-col gap-0.5 rounded-2xl bg-white p-2 dark:text-white"
          sideOffset={10}
          alignOffset={-8}
        >
          {option.actions.map((action) => (
            <DropdownMenu.Item
              key={action.id}
              className="hover:bg-gray-15 dark:hover:bg-gray-90 cursor-pointer rounded-lg px-4 py-3 outline-hidden transition-colors"
              onSelect={() => {
                option.onSelectAction(action);
                onClose();
              }}
            >
              <Text
                as="div"
                token="14/medium"
                className="text-primary whitespace-nowrap"
              >
                {action.label}
              </Text>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
}

const MENU_SEPARATOR_CLASS = 'bg-gray-30 dark:bg-gray-60 -mx-2 my-2 h-px';

function CopyShareLinkMenuItem({ onSelect }: { onSelect: () => void }) {
  return (
    <DropdownMenu.Item
      className="hover:bg-gray-15 dark:hover:bg-gray-90 cursor-pointer rounded-lg outline-hidden transition-colors"
      onSelect={onSelect}
    >
      <div className="flex items-center gap-3 px-4 py-2.5">
        <LinkIcon className="text-secondary h-5 w-5 shrink-0" />
        <Text as="div" token="14/medium" className="text-primary">
          Copy share link
        </Text>
      </div>
    </DropdownMenu.Item>
  );
}

function OpenPreviewMenuItem({ onSelect }: { onSelect: () => void }) {
  return (
    <DropdownMenu.Item
      className="hover:bg-gray-15 dark:hover:bg-gray-90 cursor-pointer rounded-lg outline-hidden transition-colors"
      onSelect={onSelect}
    >
      <div className="flex items-center gap-3 px-4 py-2.5">
        <EyeIcon className="text-secondary h-5 w-5 shrink-0" />
        <Text as="div" token="14/medium" className="text-primary">
          Open preview
        </Text>
      </div>
    </DropdownMenu.Item>
  );
}

function MoveToTrashMenuItem({
  onSelect,
  disabled = false,
}: {
  onSelect: () => void;
  disabled?: boolean;
}) {
  const item = (
    <DropdownMenu.Item
      disabled={disabled}
      className={`rounded-lg outline-hidden transition-colors ${
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'hover:bg-gray-15 dark:hover:bg-gray-90 cursor-pointer'
      }`}
      onSelect={(event) => {
        if (disabled) {
          event.preventDefault();
          return;
        }

        onSelect();
      }}
    >
      <div className="flex items-center gap-3 px-4 py-2.5">
        <TrashIcon className="text-red h-5 w-5 shrink-0" />
        <Text as="div" token="14/medium" className="text-red">
          Move to trash
        </Text>
      </div>
    </DropdownMenu.Item>
  );

  if (!disabled) {
    return item;
  }

  return (
    <AssetLibraryBlockedActionTooltip
      disabled
      side="right"
      text={ASSET_LIBRARY_DELETE_OWN_ITEM_TOOLTIP}
      className="block w-full"
    >
      {item}
    </AssetLibraryBlockedActionTooltip>
  );
}

function MoveToLibraryMenuItem({
  onSelect,
  disabled = false,
}: {
  onSelect: () => void;
  disabled?: boolean;
}) {
  const item = (
    <DropdownMenu.Item
      disabled={disabled}
      className={`rounded-lg outline-hidden transition-colors ${
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'hover:bg-gray-15 dark:hover:bg-gray-90 cursor-pointer'
      }`}
      onSelect={(event) => {
        if (disabled) {
          event.preventDefault();
          return;
        }

        onSelect();
      }}
    >
      <div className="flex items-center gap-3 px-4 py-2.5">
        <LearnIcon className="text-primary h-5 w-5 shrink-0" />
        <Text as="div" token="14/medium" className="text-primary">
          Move to Library
        </Text>
      </div>
    </DropdownMenu.Item>
  );

  if (!disabled) {
    return item;
  }

  return (
    <AssetLibraryBlockedActionTooltip
      disabled
      side="right"
      text={ASSET_LIBRARY_RESTORE_OWN_ITEM_TOOLTIP}
      className="block w-full"
    >
      {item}
    </AssetLibraryBlockedActionTooltip>
  );
}

function AssetLibraryCardMoreMenuContent({
  menuState,
  onClose,
  onMoveToTrash,
  onMoveToLibrary,
  moveToTrashDisabled = false,
  moveToLibraryDisabled = false,
}: {
  menuState: AssetLibraryCardMoreMenuState;
  onClose: () => void;
  onMoveToTrash?: () => void;
  onMoveToLibrary?: () => void;
  moveToTrashDisabled?: boolean;
  moveToLibraryDisabled?: boolean;
}) {
  const { openWithOptions, onOpenPreview, onCopyShareLink, showMoveToTrash, showMoveToLibrary } =
    menuState;
  const hasOpenWith = openWithOptions.length > 0;
  const hasPreview = Boolean(onOpenPreview);
  const hasCopyShareLink = Boolean(onCopyShareLink);
  const hasPrimaryActions = hasOpenWith || hasPreview || hasCopyShareLink;
  const hasDestructiveAction = Boolean(showMoveToTrash);
  const hasRestoreAction = Boolean(showMoveToLibrary);

  if (hasRestoreAction) {
    return (
      <MoveToLibraryMenuItem
        disabled={moveToLibraryDisabled}
        onSelect={() => {
          onMoveToLibrary?.();
          onClose();
        }}
      />
    );
  }

  return (
    <>
      {hasOpenWith ? (
        <div className="flex flex-col py-1">
          <Text as="div" token="14/medium" className="text-tertiary px-4 py-2">
            Open with
          </Text>
          {openWithOptions.map((option) =>
            option.app === 'mascot' ? (
              <OpenWithMascotOption
                key={option.app}
                option={option}
                onClose={onClose}
              />
            ) : (
              <OpenWithSingleOption
                key={option.app}
                option={option}
                onClose={onClose}
              />
            ),
          )}
        </div>
      ) : null}
      {hasOpenWith && (hasPreview || hasCopyShareLink) ? (
        <DropdownMenu.Separator className={MENU_SEPARATOR_CLASS} />
      ) : null}
      {hasPreview ? (
        <OpenPreviewMenuItem
          onSelect={() => {
            onOpenPreview?.();
            onClose();
          }}
        />
      ) : null}
      {hasCopyShareLink ? (
        <CopyShareLinkMenuItem
          onSelect={() => {
            onCopyShareLink?.();
            onClose();
          }}
        />
      ) : null}
      {hasPrimaryActions && hasDestructiveAction ? (
        <DropdownMenu.Separator className={MENU_SEPARATOR_CLASS} />
      ) : null}
      {hasDestructiveAction ? (
        <MoveToTrashMenuItem
          disabled={moveToTrashDisabled}
          onSelect={() => {
            onMoveToTrash?.();
            onClose();
          }}
        />
      ) : null}
    </>
  );
}

export interface AssetLibraryCardMoreMenuProps {
  asset: AssetLibraryDisplayItem;
  embedded: boolean;
  visible: boolean;
  isGroupDetailItem?: boolean;
  onClick?: () => void;
  onMoveToTrash?: () => void;
  onMoveToLibrary?: () => void;
}

export default function AssetLibraryCardMoreMenu({
  asset,
  embedded,
  visible,
  isGroupDetailItem = false,
  onClick,
  onMoveToTrash,
  onMoveToLibrary,
}: AssetLibraryCardMoreMenuProps) {
  const store = useStore();
  const { libraryView } = store.state;
  const {
    openMotionEditorFromJob,
    openSceneInMotionEditor,
    openComicWithVideo,
    openMascotWithMedia,
  } = useAssetLibraryOpenWithActions();
  const { canDeleteAsset } = useAssetLibraryAssetActions();
  const [open, setOpen] = useState(false);
  const moveToTrashDisabled = !canDeleteAsset(asset);
  const moveToLibraryDisabled = !canDeleteAsset(asset);

  const menuState = useMemo(
    () => {
      const resolved = resolveAssetLibraryCardMoreMenu({
        asset,
        embedded,
        libraryView,
        isGroupDetailItem,
        onClick,
        onOpenMotionEditorFromJob: openMotionEditorFromJob,
        onOpenSceneInMotionEditor: openSceneInMotionEditor,
        onOpenComic: openComicWithVideo,
        onOpenMascot: openMascotWithMedia,
      });

      if (!resolved) {
        return null;
      }

      const shareUrl = buildAssetLibraryShareUrl(asset);
      if (!shareUrl) {
        return resolved;
      }

      return {
        ...resolved,
        onCopyShareLink: () => {
          void copyAssetLibraryShareLink(shareUrl);
        },
      };
    },
    [
      asset,
      embedded,
      isGroupDetailItem,
      libraryView,
      onClick,
      openComicWithVideo,
      openMascotWithMedia,
      openMotionEditorFromJob,
      openSceneInMotionEditor,
    ],
  );

  if (!menuState) {
    return null;
  }

  const showTrigger = visible || open;

  const stopBubble = (event: MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div
      className={`transition-opacity duration-200 ease-out ${
        showTrigger ? 'opacity-100' : 'pointer-events-none opacity-0'
      }`}
      onClick={stopBubble}
      onMouseDown={stopBubble}
    >
      <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={false}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            aria-label="More options"
            className={CARD_ACTION_BUTTON_CLASS}
            onClick={stopBubble}
          >
            <ThreeDotsIcon className="text-primary h-4 w-4" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className={MENU_CONTENT_CLASS}
            align="end"
            side="bottom"
            sideOffset={4}
            collisionPadding={8}
          >
            <AssetLibraryCardMoreMenuContent
              menuState={menuState}
              onClose={() => setOpen(false)}
              onMoveToTrash={onMoveToTrash}
              onMoveToLibrary={onMoveToLibrary}
              moveToTrashDisabled={moveToTrashDisabled}
              moveToLibraryDisabled={moveToLibraryDisabled}
            />
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
